import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  VenueDealStatusActive,
  VenueDealStatusArchived,
  VenueDealStatusPaused,
  type VenueDeal,
  type VenueDealStatus,
  type VenuePublicDeal,
} from '@/lib/api/apiTypes';
import {
  formatDealDays,
  formatDealTimeRange,
  getDealTiming,
  statusLabel,
  type DealTiming,
} from './deals';
import { cn } from '@/lib/utils';
import {
  ArchiveIcon,
  BookmarkIcon,
  MoreHorizontalIcon,
  NavigationIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  ShareIcon,
} from 'lucide-react-native';
import { Pressable, View } from 'react-native';

const SKIPPED_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

export function VenueDealStatusBadge({
  status,
  timing,
}: {
  status: VenueDealStatus;
  timing?: DealTiming;
}) {
  return (
    <View
      className={cn(
        'self-start rounded-md border px-2 py-1',
        status === VenueDealStatusActive && timing?.state === 'live'
          ? 'border-orange-200 bg-orange-50'
          : status === VenueDealStatusActive
            ? 'border-green-200 bg-green-50'
            : status === VenueDealStatusPaused
              ? 'border-yellow-200 bg-yellow-50'
              : 'border-gray-200 bg-gray-50'
      )}>
      <Text
        variant="small"
        className={cn(
          'text-[11px] font-bold uppercase tracking-[1px]',
          status === VenueDealStatusActive && timing?.state === 'live'
            ? 'text-orange-700'
            : status === VenueDealStatusActive
              ? 'text-green-700'
              : status === VenueDealStatusPaused
                ? 'text-yellow-700'
                : 'text-gray-700'
        )}>
        {statusLabel(status, timing)}
      </Text>
    </View>
  );
}

export function VenueDealTimingLabel({ timing }: { timing: DealTiming }) {
  return (
    <Text variant="small" className="font-semibold text-muted-foreground">
      {timing.label}
    </Text>
  );
}

export function VenueDealCard({
  deal,
  compact = false,
  isSaving = false,
  onDirections,
  onPress,
  onSave,
  onShare,
}: {
  deal: VenuePublicDeal;
  compact?: boolean;
  isSaving?: boolean;
  onDirections?: () => void;
  onPress?: () => void;
  onSave?: () => void;
  onShare?: () => void;
}) {
  const timing = getDealTiming(deal);
  const Wrapper = onPress ? Pressable : View;
  const isLive = timing.state === 'live';
  const showActions = !compact && (onSave || onShare || onDirections);

  if (compact) {
    return (
      <Wrapper
        onPress={onPress}
        className={cn(
          'relative w-[168px] overflow-hidden rounded-xl border p-3',
          isLive
            ? 'border-orange-300 bg-orange-50/50 dark:border-orange-900/70 dark:bg-orange-950/15'
            : 'border-border bg-card'
        )}>
        {isLive ? <View className="absolute bottom-0 left-0 top-0 w-[3px] bg-orange-500" /> : null}
        <View className="flex-row items-center justify-between gap-2">
          {isLive ? (
            <View className="rounded bg-orange-500 px-1.5 py-0.5">
              <Text className="text-[10px] font-bold uppercase tracking-wide text-white">Live</Text>
            </View>
          ) : (
            <View className="rounded bg-muted px-1.5 py-0.5">
              <Text className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {timing.label}
              </Text>
            </View>
          )}
          <Text className="shrink text-right text-[11px] text-muted-foreground" numberOfLines={1}>
            {formatDealTimeRange(deal)}
          </Text>
        </View>
        <Text className="mt-2 text-[15px] font-bold leading-5 text-foreground" numberOfLines={2}>
          {deal.title}
        </Text>
        <Text className="mt-1.5 text-xs font-medium text-muted-foreground" numberOfLines={1}>
          {deal.venue_name}
        </Text>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      onPress={onPress}
      className={cn(
        'relative overflow-hidden rounded-md border bg-card p-4',
        isLive
          ? 'border-orange-300 bg-orange-50/40 shadow-lg shadow-orange-500/20 dark:border-orange-900/70 dark:bg-orange-950/10'
          : 'border-border'
      )}>
      {isLive ? <View className="absolute bottom-0 left-0 top-0 w-1 bg-orange-500" /> : null}
      <View className="flex-row items-start justify-between gap-3">
        <VenueDealStatusBadge status={deal.status} timing={timing} />
        <VenueDealTimingLabel timing={timing} />
      </View>

      <Text className="mt-4 text-lg font-bold text-foreground" numberOfLines={1}>
        {deal.title}
      </Text>
      <Text
        className={cn(
          'mt-1 text-sm font-semibold leading-5',
          deal.description.trim() ? 'text-foreground/80' : 'text-muted-foreground/60'
        )}
        numberOfLines={2}>
        {deal.description.trim() ? deal.description : 'No description added'}
      </Text>

      <View className="mt-4 gap-1">
        <Text variant="small" className="text-muted-foreground">
          {formatDealTimeRange(deal)}
        </Text>
        <Text variant="small" className="text-muted-foreground">
          {formatDealDays(deal.active_days)}
        </Text>
      </View>

      {showActions ? (
        <View className="mt-4 flex-row flex-wrap items-center gap-2">
          {onSave ? (
            <Button
              size="sm"
              variant={deal.is_saved ? 'default' : 'outline'}
              disabled={isSaving}
              onPress={onSave}>
              <Icon
                as={BookmarkIcon}
                size={14}
                className={deal.is_saved ? 'text-primary-foreground' : 'text-foreground'}
              />
              <Text>{deal.is_saved ? 'Saved' : 'Save'}</Text>
            </Button>
          ) : null}
          {onShare ? (
            <Button size="sm" variant="outline" onPress={onShare}>
              <Icon as={ShareIcon} size={14} className="text-foreground" />
              <Text>Share</Text>
            </Button>
          ) : null}
          {onDirections ? (
            <Button size="sm" variant="outline" onPress={onDirections}>
              <Icon as={NavigationIcon} size={14} className="text-foreground" />
              <Text>Directions</Text>
            </Button>
          ) : null}
        </View>
      ) : null}
    </Wrapper>
  );
}

export function BusinessVenueDealCard({
  deal,
  busy,
  onArchive,
  onEdit,
  onRestoreDate,
  onToggleStatus,
}: {
  deal: VenueDeal;
  busy: boolean;
  onArchive: () => void;
  onEdit: () => void;
  onRestoreDate?: (date: string) => void;
  onToggleStatus: () => void;
}) {
  const timing = getDealTiming(deal);
  const isActive = deal.status === VenueDealStatusActive;

  return (
    <View className="rounded-md border border-border bg-card p-4">
      <View className="flex-row items-start justify-between gap-3">
        <VenueDealStatusBadge status={deal.status} timing={timing} />
        <VenueDealTimingLabel timing={timing} />
      </View>
      <Text className="mt-3 text-base font-bold text-foreground" numberOfLines={1}>
        {deal.title}
      </Text>
      <Text
        variant="small"
        className={cn(
          'mt-1 leading-5',
          deal.description.trim() ? 'text-foreground/80' : 'text-muted-foreground/60'
        )}
        numberOfLines={2}>
        {deal.description.trim() ? deal.description : 'No description added'}
      </Text>
      <Text variant="small" className="mt-3 text-muted-foreground">
        {formatDealTimeRange(deal)} · {formatDealDays(deal.active_days)}
      </Text>
      {deal.skipped_dates.length > 0 ? (
        <View className="mt-3 gap-2 rounded-md border border-border bg-background px-3 py-2">
          <Text variant="small" className="text-muted-foreground">
            Skipped dates
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {deal.skipped_dates.map((date) => (
              <Pressable
                key={date}
                disabled={busy || !onRestoreDate}
                className="rounded-md border border-border bg-card px-2 py-1"
                onPress={() => onRestoreDate?.(date)}>
                <Text variant="small" className="text-foreground">
                  {formatSkippedDate(date)} · restore
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      ) : null}
      <View className="mt-4 flex-row items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onPress={onEdit}>
          <Icon as={PencilIcon} size={14} className="text-foreground" />
          <Text>Edit</Text>
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || deal.status === VenueDealStatusArchived}
          onPress={onToggleStatus}>
          <Icon as={isActive ? PauseIcon : PlayIcon} size={14} className="text-foreground" />
          <Text>{isActive ? 'Pause' : 'Resume'}</Text>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" disabled={busy} className="ml-auto size-9">
              <Icon as={MoreHorizontalIcon} size={16} className="text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-40">
            <DropdownMenuItem variant="destructive" onPress={onArchive}>
              <Icon as={ArchiveIcon} size={15} className="text-destructive" />
              <Text>Archive</Text>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </View>
    </View>
  );
}

function formatSkippedDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;

  return SKIPPED_DATE_FORMATTER.format(new Date(year, month - 1, day));
}
